import * as moment from 'moment';
import api from '@/utils/api';
import utilityFunctions from '@/utils/functions';
import * as Constants from '@/constants';

export default {
  name: 'ServiceInformation',
  props: ['student', 'showTooltip', 'needLevelArray', 'deliveryModeArray', 'action', 'validateForm', 'v'],
  data() {
    return {
      // service date display controls
      serviceMenu: false,
      serviceDate: null,
      // start time display controls
      startTimeDialog: false,
      startTimeModal: '',
      // end time display controls
      endTimeDialog: false,
      endTimeModal: '',
      hideFields: false,
      hideRoomField: true,
      searchInput: null,
      locationsArray: [],
      locationsLoading: false,
      sessionTime: null,
      sessionTimeEnd: null,
      absentArray: [
        { name: 'Yes', value: true },
        { name: 'No', value: false }
      ]
    };
  },
  computed: {
    isMobile() {
      return this.$mq === 'mobile';
    },
    isEditingOrCopying() {
      return this.action === 'edit' || this.action === 'copy';
    },
    needLevelErrors() {
      const errors = [];
      const needLevel = this.v.needLevel;
      if (!needLevel.$dirty) return errors;
      if (!needLevel.required) errors.push('Level of Need is required.');
      return errors;
    },
    deliveryModeErrors() {
      const errors = [];
      const deliveryMode = this.v.deliveryMode;
      if (!deliveryMode.$dirty) return errors;
      if (!deliveryMode.required) errors.push('Delivery Mode is required.');
      return errors;
    },
    roomNameErrors() {
      const errors = [];
      const roomName = this.v.roomName;
      if (!roomName.$dirty) return errors;
      if (!roomName.required) errors.push('Classroom Number is required.');
      return errors;
    },
    noShowErrors() {
      const errors = [];
      const noShow = this.v.noShow;
      if (!noShow.$dirty) return errors;
      if (!noShow.required) errors.push('No Call/No Show is required.');
      return errors;
    },
    sessionDateErrors() {
      const errors = [];
      const sessionDate = this.v.sessionDate;
      if (!sessionDate.$dirty) return errors;
      if (!sessionDate.required) errors.push('Service Date is required.');
      if (!sessionDate.isWithinOneYear) errors.push('Service Date must be within one year.');
      if (!sessionDate.isBetweenStartAndEndDate) errors.push('Service Date must be between Projected Start Date and Projected End Date.');
      if (!sessionDate.isNotFutureDate) errors.push('Service Date cannot be a future date.');
      return errors;
    },
    sessionTimeErrors() {
      const errors = [];
      const startTime = this.v.sessionTime;
      if (!startTime || !startTime.$dirty) return errors;
      if (!startTime.required) errors.push('Please enter a valid time (hh:mm tt)');
      return errors;
    },
    sessionTimeEndErrors() {
      const errors = [];
      const endTime = this.v.sessionTimeEnd;
      if (!endTime || !endTime.$dirty) return errors;
      if (!endTime.required) errors.push('Please enter a valid time (hh:mm tt).');
      if (!endTime.isSessionTimeEndGreaterThanSessionTime) errors.push('End Time must be greater than Start Time');
      return errors;
    },
    serviceLengthErrors() {
      const errors = [];
      const serviceLength = this.v.sessionMinutes;
      if (!serviceLength || !serviceLength.$dirty) return errors;
      if (!serviceLength.required) errors.push('Service Length is required.');
      if (!serviceLength.areMinutesGreaterThanZero) errors.push('Minutes must be greater than 0.');
      // validates service length doesnt exceed time in day
      // have it in component to show user how much time
      const startTime = this.student.form.serviceInformation.sessionTime ?
        utilityFunctions.convertTimeToMinutes(this.student.form.serviceInformation.sessionTime, true) : null;
      const val = this.student.form.serviceInformation.sessionMinutes;
      if (!_.isNil(startTime) && val) {
        const maxMinutes = 1440 - startTime;
        if (Number(val) > maxMinutes) errors.push(`Service Length cannot exceed ${maxMinutes} minutes.`);
      }
      if (!serviceLength.isValidMinutes) errors.push('Invalid minutes.');
      return errors;
    },
    locationErrors() {
      const errors = [];
      const location = this.v.location;
      if (!location.$dirty) return errors;
      if (!location.required) errors.push('Location is required.');
      return errors;
    },
    providerNameCheck() {
      if(this.student.form.serviceInformation.providerFirstLastName !== 'undefined undefined') {
        return this.student.form.serviceInformation.providerFirstLastName;
      }
      else {
        return this.student.form.serviceInformation.fullName;
      }
    },
  },
  mounted() {
    // if the action is edit or copy
    // set form values
    if (this.isEditingOrCopying) {
      const formValues = this.student.form.serviceInformation;
      // initialize date picker to passed in date
      this.serviceDate = moment(formValues.sessionDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      this.hideFields = formValues.needLevel === Constants.ABSENT;
      this.hideRoomField = formValues.deliveryMode !== Constants.IN_PERSON;
    }
  },
  methods: {
    clearLocationsAutoComplete() {
      if (!this.student.form.serviceInformation.location) {
        this.locationsArray = [];
      }
    },
    allowedDates(date) {
      return moment(date).isBefore(moment());
    },
    startTimeChanged() {
      // close the modal
      this.startTimeModal = false;
      // mark the field as touched
      this.v.sessionTime.$touch();
      const startTime = this.student.form.serviceInformation.sessionTime;
      const endTime = this.student.form.serviceInformation.sessionTimeEnd;
      const elapsedTime = this.student.form.serviceInformation.sessionMinutes;
      if (startTime && endTime) {
        try {
          this.student.form.serviceInformation.sessionMinutes =
            utilityFunctions.calculateServiceLength(startTime, endTime);
          // set Service Length Field as touched for validations
          this.v.sessionMinutes.$touch();
        } catch (err) {
          // dont need to do anything with the error
        }
      } else if (startTime && elapsedTime) {
        try {
          this.student.form.serviceInformation.sessionTimeEnd =
            utilityFunctions.calculateEndTime(startTime, elapsedTime);
          // set End Time as touched for validations
          this.v.sessionTimeEnd.$touch();
        } catch (err) {
          // dont need to do anything with the error
        }
      }
    },
    endTimeChanged() {
      // close the modal
      this.endTimeModal = false;
      // mark the field as touched
      this.v.sessionTimeEnd.$touch();
      const startTime = this.student.form.serviceInformation.sessionTime;
      const endTime = this.student.form.serviceInformation.sessionTimeEnd;
      const elapsedTime = this.student.form.serviceInformation.sessionMinutes;
      if (startTime && endTime) {
        try {
          this.student.form.serviceInformation.sessionMinutes =
            utilityFunctions.calculateServiceLength(startTime, endTime);
          this.v.sessionMinutes.$touch();
        } catch (err) {
          // dont need to do anything with the error
        }
      } else if (endTime && elapsedTime) {
        try {
          this.student.form.serviceInformation.sessionTime =
            utilityFunctions.calculateStartTime(endTime, elapsedTime);
          this.v.sessionTime.$touch();
        } catch (err) {
          // dont need to do anything with the error
        }
      }
    },
    sessionMinutesChanged() {
      this.v.sessionMinutes.$touch();
      const serviceLength = this.student.form.serviceInformation.sessionMinutes;
      const startTime = this.student.form.serviceInformation.sessionTime;
      // const endTime = this.student.form.serviceInformation.sessionTimeEnd;
      if (startTime && serviceLength) {
        try {
          const calculatedEndTime = utilityFunctions.calculateEndTime(startTime, serviceLength);
          // set the value in the timepicker
          this.$refs.endTimeDialog.save(calculatedEndTime);
          this.student.form.serviceInformation.sessionTimeEnd = calculatedEndTime;
          this.v.sessionTimeEnd.$touch();
        } catch (err) {
          // dont need to do anything with the error
        }
      }
    },
    serviceDateChanged(val) {
      // mark field as touched for validation
      this.student.form.serviceInformation.sessionDate = utilityFunctions.formatDate(val);
      this.v.sessionDate.$touch();
    }
  },
  watch: {
    // watches when needLevel changes
    'student.form.serviceInformation.needLevel': function (val) {
      // if ABSENT is selected from the dropdown, hide fields
      this.hideFields = val === Constants.ABSENT;
    },
    // watches when deliveryMode changes
    'student.form.serviceInformation.deliveryMode': function (val) {
      // if ABSENT is selected from the dropdown, hide fields
      this.hideRoomField = val !== Constants.IN_PERSON;
    },
    searchInput() {
      this.locationsLoading = true;
      clearTimeout(this.timeout);
      if (this.searchInput) {
        this.timeout = setTimeout(() => {
          api.get(Constants.GET_SERVICE_LOCATIONS_URL(this.searchInput))
            .then((res) => {
              this.locationsArray = res.data.data;
              this.locationsLoading = false;
            });
        }, 300);
      } else {
        this.locationsLoading = false;
      }
    },
    'student.form.serviceInformation.location': function (newVal, oldVal) {
      if (!newVal && oldVal) this.locationsArray = [];
      if (typeof newVal !== 'object') {
        this.student.form.serviceInformation.location = '';
      }
    }
  }
};



// WEBPACK FOOTER //
// ./src/components/service-log/form/service-information/service-information.js